import type {
  ArtifactOpenItem,
  DocumentArtifactSection,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'

export const MISSING_MARKER = '***'
const TOKEN_START = '\uE000'
const TOKEN_END = '\uE001'
const TOKEN_PATTERN = /\uE000(\d+)\uE001/g

/**
 * Replaces every `***` with a private-use token before markdown parsing, so
 * the marker is never read as emphasis and each occurrence keeps its index.
 */
export function tokenizeMissingMarkers(body: string): string {
  let index = 0
  return body.split(MISSING_MARKER).reduce((acc, part, partIndex) => {
    if (partIndex === 0) return part
    const token = `${TOKEN_START}${index}${TOKEN_END}`
    index += 1
    return `${acc}${token}${part}`
  }, '')
}

export function countMissingMarkers(body: string): number {
  return body.split(MISSING_MARKER).length - 1
}

export type MissingSplit =
  | { type: 'text'; text: string }
  | {
      type: 'missing'
      index: number
    }

export function splitMissingTokens(text: string): MissingSplit[] {
  const parts: MissingSplit[] = []
  let last = 0
  for (const match of text.matchAll(TOKEN_PATTERN)) {
    const start = match.index ?? 0
    if (start > last)
      parts.push({ type: 'text', text: text.slice(last, start) })
    parts.push({ type: 'missing', index: Number(match[1]) })
    last = start + match[0].length
  }
  if (last < text.length) parts.push({ type: 'text', text: text.slice(last) })
  return parts
}

export function sectionMissingReasons(
  section: DocumentArtifactSection,
  openItems: readonly ArtifactOpenItem[]
): string[] {
  return openItems
    .filter((item) => item.section_id === section.section_id)
    .map((item) => item.reason)
}

/**
 * The reasons a marker shows on hover: its own when the section's markers and
 * open items pair up one to one, otherwise every reason for the section, since
 * the writer's order can no longer be trusted to match.
 */
export function missingReasonsFor(
  reasons: readonly string[],
  index: number,
  markerCount: number
): string[] {
  if (reasons.length === 0) return []
  const own = reasons[index]
  if (reasons.length === markerCount && own !== undefined) return [own]
  return [...reasons]
}
